import React, { useState, useEffect, useCallback } from 'react';
import "../../../assets/css/patient/Testimonials.css";

// Updated to match your running Laravel backend server URL
const API_BASE = "https://backend.bizdevit.com/api";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [testiRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/testimonials`),
        fetch(`${API_BASE}/testimonials/stats`),
      ]);

      if (!testiRes.ok || !statsRes.ok) {
        throw new Error("Could not load testimonials right now.");
      }

      const testiJson = await testiRes.json();
      const statsJson = await statsRes.json();

      // Handles both a plain array response and a { data: [...] } wrapper
      setTestimonials(Array.isArray(testiJson) ? testiJson : (testiJson.data || []));
      setStats(Array.isArray(statsJson) ? statsJson : (statsJson.data || []));
      setActiveIndex(0);
    } catch (err) {
      setError(err.message || "Something went wrong while loading testimonials.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const perPage = 3;
  const totalPages = Math.max(1, Math.ceil(testimonials.length / perPage));

  const nextPage = () => setActiveIndex(p => (p + 1) % totalPages);
  const prevPage = () => setActiveIndex(p => (p - 1 + totalPages) % totalPages);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const t = setInterval(nextPage, 6000);
    return () => clearInterval(t);
  }, [activeIndex, testimonials.length]);

  if (loading) {
    return (
      <section className="testi-section">
        <div className="testi-container">
          <div className="testi-loading">Loading testimonials…</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="testi-section">
        <div className="testi-container">
          <div className="testi-error">
            <p>{error}</p>
            <button className="testi-retry-btn" onClick={fetchData}>Retry</button>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const visible = testimonials.slice(
    activeIndex * perPage,
    activeIndex * perPage + perPage
  );

  return (
    <section className="testi-section">
      <div className="testi-shape-1" />

      <div className="testi-container">

        {/* ── Header ── */}
        <div className="testi-header">
          <div className="testi-badge">
            <span className="testi-pulse-dot" />
            <span>Real Recovery Stories</span>
          </div>

          <h2 className="testi-heading">
            Trusted by Thousands Overcoming <span className="testi-highlight">Stress</span>
          </h2>
          <p className="testi-subheading">
            Real stories from youngsters, adults, and seniors who reclaimed their peace of mind with our expert therapists
          </p>
        </div>

        {/* ── Stats bar ── */}
        {stats.length > 0 && (
          <div className="testi-stats-bar">
            {stats.map((s, i) => (
              <div key={s.id ?? i} className="testi-stat">
                <span className="testi-stat-val">{s.value}</span>
                <span className="testi-stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Cards grid ── */}
        <div className="testi-grid" key={activeIndex}>
          {visible.map((t) => (
            <div className="testi-card" key={t.id}>
              <div className="testi-card-top">
                <div className="testi-avatar-initials">
                  {t.initials}
                </div>
                <div className="testi-meta">
                  <span className="testi-name">{t.name}</span>
                  <span className="testi-role">{t.role}</span>
                  <div className="testi-stars">
                    {[...Array(t.rating)].map((_, si) => (
                      <svg key={si} width="13" height="13" viewBox="0 0 24 24" fill="#ff7a00">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                      </svg>
                    ))}
                  </div>
                </div>
                <span className="testi-tag">{t.tag}</span>
              </div>

              <div className="testi-quote-mark">"</div>
              <p className="testi-text">"{t.text}"</p>

              <div className="testi-card-footer">
                <div className="testi-footer-bar" />
              </div>
            </div>
          ))}
        </div>

        {/* ── Navigation ── */}
        {totalPages > 1 && (
          <div className="testi-nav">
            <button className="testi-nav-btn" onClick={prevPage} aria-label="Previous">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div className="testi-dots">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`testi-dot${i === activeIndex ? ' testi-dot-active' : ''}`}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Page ${i + 1}`}
                />
              ))}
            </div>

            <button className="testi-nav-btn" onClick={nextPage} aria-label="Next">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}

      </div>
    </section>
  );
}